import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useThunkSelector } from 'utility/redux';
import { get } from 'lodash';
import { APPLICATION_VERSION } from 'constants/appVersion.constant';
import Analytic from '../GoogleAnalytic';
import usePageInfo from './usePageInfo';

function getTrackingID() {
  const currentEnv = process.env.REACT_APP_ENV;
  if (currentEnv === APPLICATION_VERSION.ENV.PROD) {
    return process.env.REACT_APP_MEASUREMENT_ID;
  }
  return process.env.REACT_APP_TEST_MEASUREMENT_ID || null;
}

const getUserEmail = (accounts) => {
  if (accounts && accounts.length > 0) {
    return accounts[0].username;
  }
  return null;
};

const setGoogleAnalyticsUserProperty = (accounts, moduleAccess) => {
  try {
    const unknownPlaceholder = 'null';
    const userId = getUserEmail(accounts);
    if (!userId) {
      return null;
    }
    const userGroup = get(
      moduleAccess,
      'payload.userProfile.userGroup',
      unknownPlaceholder,
    );
    const department = get(
      moduleAccess,
      'payload.userProfile.department',
      unknownPlaceholder,
    );
    const division = get(
      moduleAccess,
      'payload.userProfile.division',
      unknownPlaceholder,
    );
    const isExternal =
      get(
        moduleAccess,
        'payload.userProfile.employeeType',
        unknownPlaceholder,
      ) === 'External';
    const newUserProperties = {
      user_group: userGroup,
      department,
      division,
      email: userId,
      isExternal,
    };
    return newUserProperties;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return null;
};

function useAnalytics() {
  const [userProperties, setUserProperties] = useState(null);
  const [analytic, setAnalytic] = useState(null);

  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();

  const moduleAccess = useThunkSelector('moduleAccess');
  const location = useLocation();
  const { pageName, moduleName } = usePageInfo();

  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) {
      return;
    }

    if (moduleAccess.isSuccess) {
      const trackingID = getTrackingID();

      const googleAnalyticsUserProperty = setGoogleAnalyticsUserProperty(
        accounts,
        moduleAccess,
      );

      if (!analytic && trackingID && googleAnalyticsUserProperty) {
        const newAnalytic = new Analytic(
          trackingID,
          googleAnalyticsUserProperty.email,
          googleAnalyticsUserProperty.division,
          googleAnalyticsUserProperty.department,
          googleAnalyticsUserProperty.isExternal,
        );
        setAnalytic(newAnalytic);

        newAnalytic.setGAUserProperties(googleAnalyticsUserProperty);
        setUserProperties(googleAnalyticsUserProperty);
      }
    }
  }, [isAuthenticated, inProgress, accounts, moduleAccess, analytic]);

  useEffect(() => {
    if (userProperties && analytic) {
      const path = location.pathname;
      analytic.sendGAPageVisit(path, pageName, moduleName);
    }
  }, [location, userProperties, analytic]);

  return {
    analytic,
  };
}

export default useAnalytics;
